'use client'

import { ScriptRequestFormContext } from '@/features/prescriptions/request/ScriptRequestFormProvider'
import React, { useContext, useEffect, useState } from 'react'
import { useStore } from 'zustand'
import { Session } from 'next-auth'
import ScriptQuestionnaireStageContainer from '@/features/prescriptions/request/ScriptQuestionnaireStageContainer'
import {
  MedicineCategory,
  MedicineWithKeyAcg,
  PatientProfile,
  ProfileStatus,
} from '@/lib/apis/types'
import DocumentUploadFormStage from '@/shared/request/DocumentUploadFormStage'
import IHIVerifyFormStage from '@/shared/request/IHIVerifyFormStage'
import PersonalDetailsFormStage from '@/shared/request/PersonalDetailsFormStage'
import { FreeTextPrescriptionContentKey } from '@/features/prescriptions/PrescriptionConstants'
import { PrescriptionRequestReviewStage } from '@/features/prescriptions/request-review/PrescriptionRequestReviewStage'
import {
  PrescriptionQuizHeader,
  PrescriptionQuizPage,
  prescriptionStepMotion,
} from '@/features/prescriptions/request/PrescriptionQuizShell'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import DatabaseMedicineDisplay from '@/features/prescriptions/request-review/DatabaseMedicineDisplay'

export type CategoryDetails = {
  medicinesUnderCategory: Array<MedicineWithKeyAcg>
  category: MedicineCategory
}

type Props = {
  session: Session | null
  profile: PatientProfile | null
  profileStatus: ProfileStatus | null
  categoryDetails?: CategoryDetails
}

export default function ScriptRequestForm({
  session,
  profile,
  profileStatus,
  categoryDetails,
}: Props) {
  const store = useContext(ScriptRequestFormContext)
  if (!store)
    throw new Error(
      'ScriptRequestForm must be used within a ScriptRequestFormProvider',
    )

  const currentStageIdx = useStore(store, (s) => s.currentStageIdx)
  const stages = useStore(store, (s) => s.stages)
  const currentStage = stages[currentStageIdx]
  const selectedMedicine = useStore(store, (s) => s.selectedMedicine)
  const updateSelectedMedicine = useStore(
    store,
    (s) => s.updateSelectedMedicine,
  )
  const updateLoginState = useStore(store, (s) => s.updateLoginState)
  const reset = useStore(store, (s) => s.reset)

  // Whenever this gets mounted, reset the form. Resetting in the submission page will have a chance to throw an error
  // since some components will get their invariants invalidated
  useEffect(() => reset(), [reset])

  // When user log-in successfully, there is no other place to trigger this than listening for `session` change
  useEffect(
    () => updateLoginState(session, profile, profileStatus),
    [session, profile, profileStatus, updateLoginState],
  )

  // Load the freetext content from the local storage
  const [freetextContent, setFreetextContent] = useState<string | null>()
  useEffect(() => {
    if (!selectedMedicine) {
      setFreetextContent(localStorage.getItem(FreeTextPrescriptionContentKey))
    }
  }, [selectedMedicine])

  const reduceMotion = useReducedMotion()
  const currentStep = currentStageIdx + 1
  const totalSteps = stages.length

  if (currentStage.type === 'IHI-VERIFICATION') {
    return (
      <IHIVerifyFormStage
        currentStep={currentStep}
        serviceLabel="Request care · Prescription consultation"
        totalSteps={totalSteps}
      />
    )
  }

  const title = stageTitle(currentStage)

  function resetScrollAfterStageExit() {
    const root = document.documentElement
    const previousScrollBehavior = root.style.scrollBehavior
    const previousScrollBehaviorPriority = root.style.getPropertyPriority(
      'scroll-behavior',
    )

    // The site uses smooth scrolling globally. Reset the viewport while the
    // outgoing stage is fully transparent so a height change cannot appear as
    // the old questions scrolling away.
    root.style.setProperty('scroll-behavior', 'auto', 'important')
    void root.offsetHeight
    window.scrollTo(0, 0)

    window.requestAnimationFrame(() => {
      if (previousScrollBehavior) {
        root.style.setProperty(
          'scroll-behavior',
          previousScrollBehavior,
          previousScrollBehaviorPriority,
        )
      } else {
        root.style.removeProperty('scroll-behavior')
      }
    })
  }

  return (
    <PrescriptionQuizPage>
      <AnimatePresence
        initial={!reduceMotion}
        mode="wait"
        onExitComplete={resetScrollAfterStageExit}
      >
        <motion.section
          animate="animate"
          exit={reduceMotion ? undefined : 'exit'}
          initial={reduceMotion ? false : 'initial'}
          key={stageKey(currentStage)}
          variants={prescriptionStepMotion}
        >
          <PrescriptionQuizHeader
            currentStep={currentStep}
            description={
              currentStage.type === 'REVIEW'
                ? 'One last look — you can change anything before it goes to the doctor.'
                : undefined
            }
            title={title}
            totalSteps={totalSteps}
          />

          {currentStage.type === 'QUESTIONNAIRE' &&
            currentStage.page === 0 &&
            selectedMedicine &&
            categoryDetails && (
              <div className="mb-8 sm:mb-10">
                <DatabaseMedicineDisplay
                  category={categoryDetails.category}
                  medicine={selectedMedicine.medicine}
                  medicinesUnderCategory={
                    categoryDetails.medicinesUnderCategory
                  }
                  onUpdateMedicine={(medicine, preferredBrand) =>
                    updateSelectedMedicine({ medicine, preferredBrand })
                  }
                  preferredBrand={selectedMedicine.preferredBrand}
                  variant="quiz"
                />
              </div>
            )}

          {(() => {
            switch (currentStage.type) {
              case 'QUESTIONNAIRE':
                return (
                  <ScriptQuestionnaireStageContainer
                    stage={currentStage}
                    stageIndex={currentStageIdx}
                  />
                )
              case 'UPLOAD-DOCUMENTS':
                return <DocumentUploadFormStage />
              case 'PERSONAL-INFORMATION':
                return <PersonalDetailsFormStage />
              case 'REVIEW':
                return (
                  <PrescriptionRequestReviewStage
                    freetextContent={freetextContent ?? undefined}
                    requestedMedicine={
                      selectedMedicine && categoryDetails
                        ? {
                            type: 'database' as const,
                            onUpdateMedicine: (medicine, preferredBrand) =>
                              updateSelectedMedicine({
                                medicine,
                                preferredBrand,
                              }),
                            ...selectedMedicine,
                            ...categoryDetails,
                          }
                        : {
                            type: 'freetext' as const,
                            medicine: freetextContent!,
                            onUpdateMedicine: (value: string) => {
                              localStorage.setItem(
                                FreeTextPrescriptionContentKey,
                                value,
                              )
                              setFreetextContent(value)
                            },
                          }
                    }
                  />
                )
              default:
                return null
            }
          })()}
        </motion.section>
      </AnimatePresence>
    </PrescriptionQuizPage>
  )
}

function stageTitle(stage: { type: string; page?: number }) {
  switch (stage.type) {
    case 'QUESTIONNAIRE':
      return stage.page === 0 ? 'Your script request.' : 'A few final checks.'
    case 'UPLOAD-DOCUMENTS':
      return 'Add photos or documents.'
    case 'PERSONAL-INFORMATION':
      return 'Add your details.'
    case 'REVIEW':
      return 'Review your request.'
    default:
      return ''
  }
}

function stageKey(stage: { type: string; page?: number }) {
  return `${stage.type}-${stage.page ?? ''}`
}
